import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "../lib/utils";

// Atlas D4: 11px/700, 3px 10px padding, `rounded-badge` corner pair. Status +
// triad variants consume the A1 soft tokens and the locked paid/pending/failed
// triad (all contrast-verified in tokens.css / contrast-contract.test.ts).
const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-badge border border-transparent px-2.5 py-[3px] text-[11px] font-bold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] duration-150 motion-reduce:transition-none overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        success:
          "bg-success-soft text-success-soft-foreground border-success-soft-border",
        warning:
          "bg-warning-soft text-warning-soft-foreground border-warning-soft-border",
        info: "bg-info-soft text-info-soft-foreground border-info-soft-border",
        paid: "bg-status-paid text-status-paid-foreground",
        pending: "bg-status-pending text-status-pending-foreground",
        failed: "bg-status-failed text-status-failed-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
